import { useEffect } from 'react'

/**
 * Codex-style word-level streaming reveal: each newly revealed word fades in
 * on its own schedule instead of snapping into the paragraph.
 *
 * Implemented as a rehype plugin so it composes with Streamdown's OWN styled
 * components (overriding `components` would replace them): the plugin wraps
 * bare text-node words in `.vibe-word-fade` spans with a per-word
 * animation-delay. Delays come from a catch-up scheduler (16ms step, 96ms
 * cap — Codex's constants): behind-realtime words fade almost immediately,
 * a fresh burst staggers gently.
 *
 * The scheduler is a MODULE SINGLETON claimed per message, not a per-render
 * closure: Streamdown caches its unified processor by the serialized shape of
 * its options, so per-message plugin closures all collapse onto whichever one
 * was cached first — a captured stale scheduler silently disables the effect.
 * Only one message streams at a time in this panel, so a single slot with an
 * epoch (the claiming message's key) is sufficient and cache-proof.
 *
 * Word keys are `${textNodeOffset}:${wordOffset}` within the markdown block —
 * stable across re-parses of the growing tail block, so an already-faded
 * word keeps its cached delay and never re-animates.
 */

const FADE_STEP_MS = 16
const FADE_CAP_MS = 96
// Code renders through Streamdown's shiki pipeline — never rewrap it.
const SKIP_TAGS = new Set(['code', 'pre'])

export interface WordFadeScheduler {
  active: boolean
  getDelay: (key: string) => number
}

const createScheduler = (): WordFadeScheduler & { epoch: string; reset: () => void } => {
  const delays = new Map<string, number>()
  let nextStart = 0
  return {
    active: false,
    epoch: '',
    reset: () => {
      delays.clear()
      nextStart = 0
    },
    getDelay: (key: string) => {
      const cached = delays.get(key)
      if (cached !== undefined) return cached
      const now = performance.now()
      const start = Math.max(now, nextStart)
      const delay = Math.round(Math.min(start - now, FADE_CAP_MS))
      nextStart = start + FADE_STEP_MS
      delays.set(key, delay)
      return delay
    },
  }
}

const globalScheduler = createScheduler()

/** Claim the streaming slot for one message while it animates. The claim is
 *  keyed so a stale cleanup (unmount after another message claimed) can't
 *  switch the fade off under the new streamer. */
export const useWordFade = (messageKey: string, active: boolean): void => {
  useEffect(() => {
    if (!active) return
    if (globalScheduler.epoch !== messageKey) {
      globalScheduler.reset()
      globalScheduler.epoch = messageKey
    }
    globalScheduler.active = true
    return () => {
      if (globalScheduler.epoch === messageKey) globalScheduler.active = false
    }
  }, [messageKey, active])
}

interface HastNode {
  type: string
  tagName?: string
  value?: string
  children?: HastNode[]
  position?: { start?: { offset?: number } }
  properties?: Record<string, unknown>
}

/** Transform factory — exported for tests; production uses the singleton via
 *  WORD_FADE_REHYPE_PLUGIN. Inert while the scheduler is inactive (history
 *  rows and the final post-stream parse render plain — no span weight). */
export const rehypeWordFade = (scheduler: WordFadeScheduler) => () => (tree: HastNode) => {
  if (!scheduler.active) return

  const walk = (node: HastNode) => {
    if (node.type === 'element' && SKIP_TAGS.has(node.tagName ?? '')) return
    if (!node.children) return

    const next: HastNode[] = []
    for (const child of node.children) {
      if (child.type !== 'text' || !child.value?.trim()) {
        walk(child)
        next.push(child)
        continue
      }
      const base = child.position?.start?.offset ?? 0
      let cursor = 0
      for (const part of child.value.split(/(\s+)/)) {
        if (!part) continue
        if (!part.trim()) {
          next.push({ type: 'text', value: part })
        } else {
          const delay = scheduler.getDelay(`${base}:${cursor}`)
          next.push({
            type: 'element',
            tagName: 'span',
            properties: {
              className: ['vibe-word-fade'],
              style: `animation-delay:${delay}ms`,
            },
            children: [{ type: 'text', value: part }],
          })
        }
        cursor += part.length
      }
    }
    node.children = next
  }

  walk(tree)
}

export const WORD_FADE_REHYPE_PLUGIN = rehypeWordFade(globalScheduler)
